import React, { useState } from "react";
import { H3, Input } from "@bootstrap-styled/v4";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { logError, LogCategory } from "utils/sentryLogger";
import { Button, Modal } from "react-bootstrap";
import { ConsensusActivityRender } from "components/pages/Session/Activity/activities/ConsensusActivity/ConsensusActivityRender";
import { CalculatorRender } from "components/pages/Session/Activity/activities/CalculatorActivity/Calculator/CalculatorRender";
import { useButtonIds } from "hooks/activities/calculator/useButtonMaps/useButtonMaps";
import { request } from "hooks/useOkoApi/useOkoApi";
import { buildPreviewChallenge, buttonIdsToStr, constants } from "utils";
import styled from "styled-components";
import { CloseButton } from "components/shared/CloseButton/CloseButton";
import { QuestionCarouselNav } from "components/shared/QuestionCarouselNav/QuestionCarouselNav";

/**
 * What a challenge-issue submission carries.
 *
 * `report_location` is where the person raising it was reading the question:
 * the Educator Dashboard for a teacher, the admin prompt console for staff
 * judging a prompt. It is data rather than a mode flag, and it keeps a real
 * feedback dataset from recording an admin's note as a teacher's.
 *
 * Exported because the modal that raises it has no trigger in this worktree, so
 * this is the only place the payload can be read.
 */
export const challengeFeedbackPayload = ({
  details,
  challengeId,
  sessionId,
  reportLocation = "Educator Dashboard",
}) => ({
  feedback_type: "challenge_issue",
  details,
  challenge_id: challengeId,
  session_id: sessionId,
  report_location: reportLocation,
});

export const SessionHistoryChallenge = ({
  value,
  challengeDetails,
  currentQuestionIndex,
  setCurrentQuestionIndex,
  reportLocation,
}) => {
  const [internalIndex, setInternalIndex] = useState(0);

  // Use props if provided, otherwise fall back to internal state
  const currentIndex = currentQuestionIndex ?? internalIndex;
  const setCurrentIndex = setCurrentQuestionIndex ?? setInternalIndex;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailsText, setDetailsText] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const buttonIds = useButtonIds();

  if (!challengeDetails || !challengeDetails.length > 0) {
    return <></>;
  }

  const currentChallengeDetail = challengeDetails[currentIndex];
  const handlePrev = () => setCurrentIndex(i => Math.max(i - 1, 0));
  const handleNext = () => setCurrentIndex(i => Math.min(i + 1, challengeDetails.length - 1));

  const handleSubmitFeedback = async () => {
    if (!detailsText) return;
    setIsLoading(true);
    await request("feedback/submit_custom_feedback", {
      payload: challengeFeedbackPayload({
        details: detailsText,
        challengeId: currentChallengeDetail.challenge.id,
        sessionId: value.session_id,
        reportLocation,
      }),
    }).then(() => {
      setIsModalOpen(false);
      setDetailsText(null);
    }).catch((e) => {
      console.error(e);
      logError("Failed to submit challenge feedback", e, {
        category: LogCategory.ERROR,
        sessionId: value.session_id,
        challengeId: currentChallengeDetail?.challenge?.id,
      });
    }).finally(() => setIsLoading(false));
  };

  const handleClose = () => {
    if (isLoading) return;
    setIsModalOpen(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>

      <div style={{ marginTop: "16px", marginBottom: "16px" }}>
        <QuestionCarouselNav
          onPrev={handlePrev}
          onNext={handleNext}
          canPrev={currentIndex !== 0}
          canNext={currentIndex !== challengeDetails.length - 1}
        >
          <span style={{ fontSize: "20px" }}>
            <span style={{ fontWeight: "bold" }}>Session Questions</span> ({currentIndex + 1} of {challengeDetails.length})
          </span>
        </QuestionCarouselNav>
      </div>
      {value.activity_type === constants.CONSENSUS_ENUM ? (
        <ConsensusActivityRender
          challenge={buildPreviewChallenge(currentChallengeDetail.challenge)}
          onMCAnswer={() => { }}
          onORAnswer={() => { }}
          isMathJaxSet={false} // Needed to prevent race condition on MathJax typesetting.
          sessionId={value.session_id}
          showPromptFormula
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "24px" }}>
          <H3
            className="mb-2 text-center"
            style={{ textShadow: "0px 1px 3px #fff" }}
          >
            Try to make the number {currentChallengeDetail.challenge.target_answer}.
          </H3>
          <CalculatorRender
            buttonIds={buttonIdsToStr(buttonIds)}
            bannedButtonIds={currentChallengeDetail.challenge.broken_keys}
            formula={""}
            clear={() => { }}
            onButtonClick={() => { }}
            buttonAnimTrigger={{}}
          />
        </div>
      )}
      <ModalContainer show={isModalOpen}>
        {isLoading && <div style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999
        }}>
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            size="5x"
          />
        </div>}
        <Header>
          <CloseButton
            onClick={handleClose}
            color={'#000'}
            width={24}
            height={24}
            customCss={`
              position: initial;
            `}
          />
          <span style={{ fontSize: "20px", fontWeight: 700, textAlign: 'center', width: '100%' }}>Hey there! Tell us what you think.</span>
        </Header>
        <Body>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Input placeholder="Leave your feedback here…What worked well? What could be improved? Your thoughts help make this better."
              maxLength={500}
              type="textarea"
              style={{ height: "100px", padding: "0.75rem 1rem", minHeight: '210px', fontWeight: 300, fontSize: '0.875rem' }}
              onChange={(e) => setDetailsText(e.target.value)}
              value={detailsText}
            />
          </div>
        </Body>
        <Footer>
          <Button
            onClick={handleSubmitFeedback}
            style={{
              backgroundColor: '#605DEC',
              border: "none",
              fontSize: "0.875rem",
              padding: "0.5rem 1rem",
              cursor: isLoading || !detailsText ? "not-allowed" : "pointer",
              opacity: isLoading || !detailsText ? 0.5 : 1,
              margin: 0,
            }}
          >
            Submit Feedback
          </Button>
        </Footer>
      </ModalContainer>
    </div>
  );
};

const ModalContainer = styled(Modal)`
  width: 100%;

  > div {
    max-width: 388px;

    .modal-content {
      border-radius: 1rem;
    }
  }
`

const Body = styled(Modal.Body)`
  padding: 1rem 2rem;
`

const Footer = styled(Modal.Footer)`
  width: 100%;
  display: center;
  align-items: center;
  justify-content: center;
  border: none;
  padding-top: 0;
  padding-bottom: 1rem;
`


const Header = styled(Modal.Header)`
  display: flex;
  flex-direction: column;
  padding: 1rem 2rem 0;
  gap: 1rem;  
  align-items: flex-end;
  justify-content: flex-end;
  height: 100px;
  border: none;
  margin: 0;
`
